import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  CategoryDTO,
  PackageDTO,
  PricingModelDTO,
  fetchCategories,
} from "../CategoryService";
import PackageList from "./PackageList";
import PackageForm from "./PackageForm";
import PricingModelForm from "./PricingModelForm";
import PackageDetail from "./PackageDetail";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const PackageManager: React.FC = () => {
  const [packages, setPackages] = useState<PackageDTO[]>([]);
  const [categories, setCategories] = useState<CategoryDTO[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<PackageDTO | null>(
    null
  );
  const [selectedPricingModels, setSelectedPricingModels] = useState<
    PricingModelDTO[]
  >([]);
  const [showPackageForm, setShowPackageForm] = useState<boolean>(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  // True while the pricing/date form has edits that are not saved yet
  const pricingDirty = useRef<boolean>(false);
  const UNSAVED_PRICING_MESSAGE =
    "You have UNSAVED date/pricing changes. They will be lost if you continue.\n\nPress Cancel and use 'Save dates & pricing' first to keep them.";

  const navigate = useNavigate();

  const loadCategories = useCallback(async () => {
    try {
      if (apiUrl) {
        const response = await fetchCategories(apiUrl);
        setCategories(response);
        const allPackages = response.flatMap((category) => category.packages);
        setPackages(allPackages);
        console.log("Categories loaded:", response);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleAddPackage = useCallback(() => {
    setSelectedPackage(null);
    setSelectedPricingModels([]);
    setShowPackageForm(true);
    setIsEditing(false);
    console.log("Adding package");
  }, []);

  const handleEditPackage = useCallback((pkg: PackageDTO) => {
    setSelectedPackage(pkg);
    setSelectedPricingModels(
      Array.isArray(pkg.prices)
        ? pkg.prices.map((price) => ({
            ...price,
            dateRanges: price.dateRanges, // Ensure dateRanges are included
          }))
        : []
    );
    setShowPackageForm(true);
    setIsEditing(true);
    console.log("Editing package", pkg);
  }, []);

  const handleDeletePackage = useCallback(
    async (packageId: number) => {
      try {
        await axios.delete(`${apiUrl}/Package/deletePackage/${packageId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        setPackages(packages.filter((pkg) => pkg.id !== packageId));
      } catch (error) {
        console.error("Error deleting package", error);
      }
    },
    [apiUrl, packages]
  );

  const handleViewPackage = useCallback((pkg: PackageDTO) => {
    setSelectedPackage(pkg);
    setShowPackageForm(false);
    setIsEditing(false);
  }, []);

  const handlePackageAdded = useCallback(
    (newPackage: PackageDTO) => {
      const safePrices = Array.isArray(newPackage.prices)
        ? newPackage.prices
        : [];
      if (isEditing) {
        setPackages(
          packages.map((pkg) =>
            pkg.id === newPackage.id
              ? {
                  ...newPackage,
                  prices: safePrices.map((price) => ({
                    ...price,
                    dateRanges: price.dateRanges, // Ensure dateRanges are included
                  })),
                }
              : pkg
          )
        );
      } else {
        setPackages([
          ...packages,
          {
            ...newPackage,
            prices: safePrices.map((price) => ({
              ...price,
              dateRanges: price.dateRanges, // Ensure dateRanges are included
            })),
          },
        ]);
      }
      setShowPackageForm(false);
      setSelectedPackage(null);
      navigate("/newPackageManager");
    },
    [isEditing, packages, navigate]
  );

  const handlePricingModelsUpdated = useCallback(
    (updatedPricingModels: PricingModelDTO[]) => {
      if (selectedPackage) {
        const updatedPackage = {
          ...selectedPackage,
          prices: updatedPricingModels,
        };
        // Only refresh the list copy. Replacing selectedPackage would re-initialise the package form
        // above and throw away any package fields the manager is in the middle of editing.
        setPackages(
          packages.map((pkg) =>
            pkg.id === updatedPackage.id ? updatedPackage : pkg
          )
        );
      }
    },
    [selectedPackage, packages]
  );

  const handleCancel = useCallback(() => {
    if (pricingDirty.current && !window.confirm(UNSAVED_PRICING_MESSAGE)) {
      return;
    }
    setShowPackageForm(false);
    setSelectedPackage(null);
    setSelectedPricingModels([]);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedPackage(null);
  }, []);

  const memoizedPackages = useMemo(() => packages, [packages]);
  const memoizedCategories = useMemo(() => categories, [categories]);

  return (
    <div className="mb-5 pb-5">
      {showPackageForm && (
        <PackageForm
          onPackageAdded={handlePackageAdded}
          categories={memoizedCategories}
          setCategories={setCategories}
          initialData={selectedPackage ?? undefined}
          initialImageUrls={selectedPackage?.imageUrls ?? []}
          isEditing={isEditing}
          handleCancel={handleCancel}
          beforeSubmit={() => !pricingDirty.current || window.confirm(UNSAVED_PRICING_MESSAGE)}
        />
      )}
      {!showPackageForm && !selectedPackage && (
        <PackageList
          packages={memoizedPackages}
          onAddPackage={handleAddPackage}
          onEditPackage={handleEditPackage}
          onDeletePackage={handleDeletePackage}
          onViewPackage={handleViewPackage}
        />
      )}
      {selectedPackage && !showPackageForm && !isEditing && (
        <PackageDetail pkg={selectedPackage} onClose={handleCloseDetail} />
      )}
      {selectedPackage && isEditing && (
        <PricingModelForm
          packageId={selectedPackage.id!}
          onPricingModelsAdded={handlePricingModelsUpdated}
          isEditing={isEditing}
          initialData={selectedPricingModels}
          onDirtyChange={(dirty) => {
            pricingDirty.current = dirty;
          }}
        />
      )}
    </div>
  );
};

export default PackageManager;
